import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AuditType } from '../../api';
import { getAuditTypeIcon } from '../utils';
import { FilterMenu } from '../../common/filter/FilterMenu';

type Group = {
  id: string;
  labelKey: string;
  types: AuditType[];
};

const GROUPS: Group[] = [
  {
    id: 'orders',
    labelKey: 'auditlog.filter_groups.orders',
    types: [
      AuditType.ORDER_CREATED,
      AuditType.ORDER_UPDATED,
      AuditType.ORDER_DELETED,
      AuditType.PROJECT_SELECTION_UPDATED,
    ],
  },
  {
    id: 'events',
    labelKey: 'auditlog.filter_groups.events',
    types: [AuditType.EVENT_CREATED, AuditType.EVENT_UPDATED, AuditType.EVENT_DELETED],
  },
  {
    id: 'vouchers',
    labelKey: 'auditlog.filter_groups.vouchers',
    types: [
      AuditType.VOUCHER_CREATED_FROM_ORDER,
      AuditType.VOUCHER_CONVERTED_TO_ORDER,
      AuditType.ORDER_CONVERTED_TO_VOUCHER,
      AuditType.ORDER_CREATED_FROM_VOUCHER,
      AuditType.EXTERNAL_VOUCHER_VERIFIED,
      AuditType.EXTERNAL_VOUCHER_REJECTED,
    ],
  },
  {
    id: 'payment_links',
    labelKey: 'auditlog.filter_groups.payment_links',
    types: [AuditType.PAYMENT_LINK_CREATED, AuditType.PAYMENT_LINK_PAID],
  },
  {
    id: 'courses',
    labelKey: 'auditlog.filter_groups.courses',
    types: [AuditType.COURSE_ORDER_CREATED, AuditType.COURSE_ORDER_PAID, AuditType.COURSE_ORDER_DELETED],
  },
  {
    id: 'passes',
    labelKey: 'auditlog.filter_groups.passes',
    types: [
      AuditType.PASS_CREATED,
      AuditType.PASS_UPDATED,
      AuditType.PASS_PAUSED,
      AuditType.PASS_RESUMED,
      AuditType.PASS_ARCHIVED,
      AuditType.PASS_RENEWED,
      AuditType.PASS_RENEWAL_FAILED,
      AuditType.PASS_PUNCH_CANCELED,
      AuditType.PASS_PUNCH_RESTORED,
      AuditType.PASS_PUNCHES_ADJUSTED,
      AuditType.PASS_SUBSCRIPTION_CANCELED,
      AuditType.PASS_SUBSCRIPTION_REACTIVATED,
      AuditType.PASS_GUEST_PUNCH_CONSUMED,
      AuditType.PASS_GUEST_PUNCH_RESTORED,
      AuditType.PASS_SWAPPED,
      AuditType.PASS_SWAP_SCHEDULED,
      AuditType.PASS_SWAP_CANCELED,
      AuditType.ORDER_CREATED_FROM_PASS,
    ],
  },
  {
    id: 'checkins',
    labelKey: 'auditlog.filter_groups.checkins',
    types: [AuditType.CHECKIN_CREATED, AuditType.CHECKIN_UPDATED],
  },
  {
    id: 'refunds',
    labelKey: 'auditlog.filter_groups.refunds',
    types: [AuditType.REFUND_SUCCEEDED, AuditType.REFUND_PENDING, AuditType.REFUND_FAILED, AuditType.REFUND_BLOCKED],
  },
  {
    id: 'users',
    labelKey: 'auditlog.filter_groups.users',
    types: [
      AuditType.USER_CREATED,
      AuditType.USER_UPDATED,
      AuditType.USER_ROLE_CHANGED,
      AuditType.USER_BLOCKED,
      AuditType.USER_UNBLOCKED,
      AuditType.USER_PASSWORD_RESET,
    ],
  },
  {
    id: 'order_pages',
    labelKey: 'auditlog.filter_groups.order_pages',
    types: [
      AuditType.ORDER_PAGE_UPDATED,
      AuditType.ORDER_PAGE_ARCHIVED,
      AuditType.ORDER_PAGE_RESTORED,
      AuditType.PASS_ORDER_PAGE_UPDATED,
      AuditType.PASS_ORDER_PAGE_ARCHIVED,
    ],
  },
];

type AuditTypeFilterProps = {
  value: AuditType[] | undefined;
  onChange: (types: AuditType[] | undefined) => void;
};

export const AuditTypeFilter = ({ value, onChange }: AuditTypeFilterProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Set<AuditType>>(new Set(value ?? []));

  useEffect(() => {
    setDraft(new Set(value ?? []));
  }, [value]);

  const totalCount = useMemo(() => GROUPS.reduce((s, g) => s + g.types.length, 0), []);
  const selectedCount = value?.length ?? 0;
  const isActive = selectedCount > 0;

  const summaryLabel = t('auditlog.filters.audit_type');
  const secondaryLabel = isActive
    ? t('auditlog.filters.selected_count', { count: selectedCount, total: totalCount })
    : t('auditlog.filters.all_types');

  const handleToggleType = (type: AuditType) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const handleToggleGroup = (group: Group, checked: boolean) => {
    setDraft((prev) => {
      const next = new Set(prev);
      group.types.forEach((type) => {
        if (checked) next.add(type);
        else next.delete(type);
      });
      return next;
    });
  };

  const handleApply = () => {
    onChange(draft.size === 0 ? undefined : Array.from(draft));
  };

  const handleReset = () => {
    onChange(undefined);
  };

  const handleDialogReset = () => {
    setDraft(new Set());
  };

  const handleDialogOpen = () => {
    setDraft(new Set(value ?? []));
  };

  return (
    <FilterMenu
      name="AuditType"
      isActive={isActive}
      summaryLabel={summaryLabel}
      secondaryLabel={secondaryLabel}
      onReset={handleReset}
      onDialogReset={handleDialogReset}
      onDialogOpen={handleDialogOpen}
      onApply={handleApply}>
      <Box sx={{ px: 1, py: 0.5 }}>
        {GROUPS.map((group) => {
          const selectedInGroup = group.types.filter((type) => draft.has(type)).length;
          const allSelected = selectedInGroup === group.types.length;
          const someSelected = selectedInGroup > 0 && !allSelected;
          return (
            <Accordion key={group.id} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <FormControlLabel
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  control={
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(_, checked) => handleToggleGroup(group, checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {t(group.labelKey)}
                      {selectedInGroup > 0 && ` (${selectedInGroup}/${group.types.length})`}
                    </Typography>
                  }
                />
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, paddingInlineStart: 4 }}>
                <Stack spacing={0}>
                  {group.types.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox size="small" checked={draft.has(type)} onChange={() => handleToggleType(type)} />
                      }
                      label={
                        <Typography variant="body2">
                          <span style={{ marginInlineEnd: 12 }}>{getAuditTypeIcon(type)}</span>
                          {t(`auditlog.audit_types.${type}`)}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </FilterMenu>
  );
};
